import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useFieldContext } from '~/common/form/formContexts';

import Input from './InputBase/Input';

type TextInputProps = React.ComponentProps<typeof Input> & {
  errorMessage?: string;
  defaultValue?: string;
  useReactHooksForm?: boolean;
  useTanstackForm?: boolean;
  rules?: Record<string, any>;
  name?: string;
};

const TextField = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<TextInputProps>
>(({ useReactHooksForm = false, useTanstackForm = false, ...props }, ref) => {
  if (useReactHooksForm) {
    return <HookFormTextField {...props} ref={ref} />;
  }
  if (useTanstackForm) {
    return <TanstackFormTextField {...props} ref={ref} />;
  }
  return <Input ref={ref} {...props} />;
});

const HookFormTextField = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<TextInputProps>
>(({ name, rules, isDisabled, ...props }, ref) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ onChange, ...hooksProps }, { invalid }) => (
        <Input
          error={invalid}
          errorMessage={errors[name]?.message}
          {...props}
          {...hooksProps}
          ref={ref}
          isDisabled={isDisabled}
          onChange={(e) => {
            onChange(e.target?.value || '');
          }}
        />
      )}
    />
  );
});

const TanstackFormTextField = forwardRef<
  React.ElementRef<typeof Input>,
  Readonly<TextInputProps>
>(({ isDisabled, ...props }, ref) => {
  const field = useFieldContext<string>();
  const errorMessage = field.state.meta.errors[0] as string | undefined;
  return (
    <Input
      {...props}
      ref={ref}
      value={field.state.value ?? ''}
      error={field.state.meta.errors.length > 0}
      errorMessage={errorMessage}
      isDisabled={isDisabled}
      onChange={(e) => field.handleChange(e.target.value)}
      onBlur={field.handleBlur}
    />
  );
});

export default TextField;
